import React from 'react'

import styled, { css } from 'styled-components'

export interface RadioInputProps {
  checked: boolean
  disabled?: boolean
  className?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  id: string
  name: string
  children?: React.ReactNode
  label?: React.ReactNode
  extra?: React.ReactNode
  footer?: React.ReactNode
}

const HiddenRadio = styled.input.attrs({ type: 'radio' })<{
  disabled?: boolean
}>`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  margin: 0;
  cursor: pointer;
  z-index: -1;
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}
`

const Container = styled.label<{ checked: boolean; disabled?: boolean }>`
  position: relative;
  cursor: pointer;
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px 12px;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  min-width: 288px;
  max-width: 600px;
  border-radius: 16px;
  border: 1px solid
    ${({ checked, theme }) =>
      checked ? theme.color.brandGreen : theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};

  & > * {
    color: ${({ theme }) => theme.color.darkGrey};
  }

  &:hover {
    background: ${({ theme }) => theme.color.hoverWhite};
  }

  &:focus-within {
    border: 1px solid ${({ theme }) => theme.color.brandGreen};
  }
`

const Content = styled.div`
  display: flex;
  flex: 1 0 0;
  gap: 12px;
  align-items: flex-start;
  min-width: 256px;
`

const Visual = styled.div<{ checked: boolean; disabled?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 150ms ease-in-out;
  background-color: transparent;
  pointer-events: none;
  ${({ checked, disabled, theme }) =>
    !disabled &&
    css`
      ${Container}:hover & {
        background-color: ${checked
          ? theme.color.lightGreen
          : theme.color.lightGrey};
      }
      ${Container}:active & {
        background-color: ${checked ? theme.color.lightGreen : 'transparent'};
      }
    `}
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}
`

const Ring = styled.div<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ checked, theme }) =>
    checked ? theme.color.lightGreen : 'transparent'};
  border: 1px solid
    ${({ checked, theme }) =>
      checked ? theme.color.brandGreen : theme.color.darkGrey};
  transition: border-color 150ms ease-in-out;

  ${Container}:focus-within & {
    border: 1px solid ${({ theme }) => theme.color.brandGreen};
  }
  ${Container}:active & {
    border: 1px solid ${({ theme }) => theme.color.slateGrey};
  }
  ${Container}:active & {
    background-color: ${({ theme }) => theme.color.lightGrey}
`

const Dot = styled.div<{ checked: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  transition: all 150ms ease-in-out;
  background-color: ${({ checked, theme }) =>
    checked ? theme.color.brandGreen : 'transparent'};
  transform: scale(${({ checked }) => (checked ? 1 : 0)});
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 0 0;
  min-width: 0;
`

export const TaskTypeRadio: React.FC<RadioInputProps> = ({
  checked,
  disabled = false,
  onChange,
  id,
  name,
  className,
  children,
  label,
  extra,
  footer,
}) => (
  <Container checked={checked} className={className} htmlFor={id}>
    <HiddenRadio
      id={id}
      name={name}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
    <Content>
      <Visual checked={checked} disabled={disabled}>
        <Ring checked={checked}>
          <Dot checked={checked} />
        </Ring>
      </Visual>
      <TextWrapper>
        {label}
        {children}
      </TextWrapper>
    </Content>
    {extra}
    {footer}
  </Container>
)
